"use client"

import { useState } from "react";
import emailjs from "emailjs-com";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, MessageCircle, Users, Clock, CheckCircle } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import { onSendEmailToBackend } from "../utils/api";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    agreement: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const sendEmail = async (e) => {
    e.preventDefault()
    if (!formData.agreement) {
      toast.error("Please agree to the terms before submitting.")
      return
    }

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.")
      return
    }

    setIsSubmitting(true)

    try {
      await onSendEmailToBackend(formData)
      toast.success("Message sent successfully!")
      setFormData({ name: "", email: "", message: "", agreement: false })
    } catch (error) {
      console.log(error)
      toast.error("Failed to send message. Please try again later.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: <MapPin className="w-5 h-5" />,
      title: "Visit Us",
      details: ["2/58B, Mutharamman Koil Street Puthur", "Kavalkinaru-627105."],
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <Phone className="w-5 h-5" />,
      title: "Call Us",
      details: ["+ 91 8438501830"],
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Email Us",
      details: ["tactosinfo@gmail.com"],
      color: "from-purple-500 to-pink-500",
    },
  ]

  const features = [
    {
      icon: <MessageCircle className="w-5 h-5" />,
      title: "Quick Response",
      description: "We respond within 24 hours",
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Expert Team",
      description: "Dedicated support specialists",
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "24/7 Support",
      description: "Round-the-clock assistance",
    },
  ]

  return (
    <div className="">
      <ToastContainer position="top-center" autoClose={3000} />
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-sm text-gray-600 mb-8">
                Ready to take your business to the next level? We're here to help you succeed.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-xl bg-gradient-to-r ${info.color} text-white group-hover:scale-110 transition-transform duration-300`}
                    >
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{info.title}</h3>
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-gray-600 mb-1">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
          >
            {/* Form Header */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 py-4">
              <h3 className="text-lg font-semibold text-white mb-2">Send us a Message</h3>
              <p className="text-gray-300">Fill out the form below and we'll get back to you soon</p>
            </div>

            {/* Form Content */}
            <form onSubmit={sendEmail} className="p-4 space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors hover:border-gray-400"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors hover:border-gray-400"
                />
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us about your project or inquiry..."
                  required
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors hover:border-gray-400 resize-none"
                />
              </div>

              {/* Agreement Checkbox */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreement"
                  name="agreement"
                  checked={formData.agreement}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="agreement" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                  I agree to the{" "}
                  <Link to="/term" className="text-blue-600 hover:text-blue-700 underline">
                    terms and conditions
                  </Link>{" "}
                  and consent to being contacted regarding my inquiry.
                </label>
              </div>

              {/* Submit Button */}
           
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-[#020c1b] hover:bg-[#0d1a2e] text-white font-medium py-2.5 px-4 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="w-4 h-4 text-[#fd3e3d]" />
                  </>
                )}
              </motion.button>

              {/* Success Message */}
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 inline text-green-500 mr-1" />
                  We typically respond within 24 hours
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Contact
